import type { Book } from "@/data/books";
import { moodById } from "@/lib/moods";

/** Boost score for a book that matches the active mood. */
export const moodBoost = (book: Book, moodId: string | null): number => {
  const m = moodById(moodId);
  if (!m) return 0;
  return m.match(book) ? 35 : -10;
};

/** Re-rank a pool of books by mood match while keeping baseline ordering for ties. */
export const rankByMood = <T extends Book>(books: T[], moodId: string | null): T[] => {
  if (!moodId) return books;
  const m = moodById(moodId);
  if (!m) return books;
  return [...books].sort((a, b) => Number(m.match(b)) - Number(m.match(a)));
};

export interface ReaderSignals {
  favoriteTropes: string[];
  favoriteSubgenres: string[];
  emotionalPreference: string | null;
  spicePreference: number | null;
  savedBookIds: string[];
  readBookIds: string[];
  dismissedBookIds: string[];
}

const lower = (s: string) => s.toLowerCase().trim();

const overlapCount = (a: string[] = [], b: string[] = []) => {
  if (!a.length || !b.length) return 0;
  const set = new Set(b.map(lower));
  let n = 0;
  for (const x of a) if (set.has(lower(x))) n++;
  return n;
};

/** Returns 0-100 match score for a book given reader signals. */
export const matchScore = (book: Book, s: ReaderSignals): number => {
  let score = 30; // baseline

  const tropeHits = overlapCount(book.tropes, s.favoriteTropes);
  score += Math.min(tropeHits * 14, 35);

  const sgHits = overlapCount(book.subgenre ?? [], s.favoriteSubgenres);
  score += Math.min(sgHits * 12, 24);

  if (s.emotionalPreference && book.emotionalIntensity === s.emotionalPreference) {
    score += 10;
  }
  if (s.spicePreference != null) {
    const diff = Math.abs(book.spiceLevel - s.spicePreference);
    score += diff === 0 ? 8 : diff === 1 ? 4 : 0;
  }

  // Boost when overlapping with previously saved books' tropes
  if (book.featured) score += 3;

  // Slight randomization keeps the feed feeling alive but stable per session
  return Math.min(99, Math.max(40, Math.round(score)));
};

/** Auto-derive emotional discovery labels from existing book fields. */
export const emotionalTagsFor = (book: Book): string[] => {
  const tags: string[] = [];
  const tropes = (book.tropes ?? []).map(lower).join(" | ");
  const intensity = book.emotionalIntensity;
  const spice = book.spiceLevel;

  if (intensity === "devastating") tags.push("Will emotionally ruin you");
  if (intensity === "emotional" && spice >= 2) tags.push("Slow emotional destruction");
  if (intensity === "light") tags.push("Kick-your-feet romance");

  if (/possessive|obsess|stalker|mafia|alpha|dominant/.test(tropes)) {
    tags.push("Touch her and die energy");
  }
  if (/forbidden|enemies|secret|rivals/.test(tropes)) {
    tags.push("Obsession-level tension");
  }
  if (/healing|hurt\/comfort|comfort|grovel|second chance/.test(tropes)) {
    tags.push("Comfort after chaos");
  }
  if (/slow burn/.test(tropes)) tags.push("Slow-burn devastation");

  return tags.slice(0, 2);
};

export const filterFeedBooks = (books: Book[], s: ReaderSignals): Book[] =>
  books.filter(
    (b) =>
      !s.dismissedBookIds.includes(b.id) &&
      !s.readBookIds.includes(b.id),
  );

export const rankByMatch = (books: Book[], s: ReaderSignals): Book[] =>
  [...books]
    .map((b) => ({ b, score: matchScore(b, s) }))
    .sort((a, z) => z.score - a.score)
    .map((x) => x.b);

/** Pick the reader's top trope/subgenre for "Because You Love X". */
export const topAffinity = (s: ReaderSignals): { trope?: string; subgenre?: string } => ({
  trope: s.favoriteTropes[0],
  subgenre: s.favoriteSubgenres[0],
});

/** Build a personality label from reader signals. */
export const readerPersonality = (s: ReaderSignals): string => {
  const t = (s.favoriteTropes[0] || "").toLowerCase();
  const intensity = s.emotionalPreference;
  if (intensity === "devastating" && /slow burn/.test(t)) return "Emotionally Devastating Slow Burn Addict";
  if (/mafia/.test(t)) return "Obsessive Mafia Romance Collector";
  if (/morally gray|alpha|possessive|dominant/.test(t)) return "Morally Gray Men Enthusiast";
  if (intensity === "devastating") return "Emotionally Wrecked Romantic";
  if (intensity === "light") return "Cozy Romance Lover";
  return "Romance Explorer";
};
